/**
 * Sticky bottom bar shown while ≥1 customer is selected. Totals are computed
 * server-side over the full selection (including rows hidden by the current
 * filter) — see `services/customers.service.summary`.
 */
import { forwardRef, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import Currency from "components/format/currency/currency";
import { summary } from "services/customers.service";
import { usePresenceRailWidth } from "features/presence/use-presence-rail-width";

const DEBOUNCE_MS = 300;

type Totals = {
  turnover: number;
  purchaseTurnover: number | null;
  debt: number;
  overdueDebt: number;
};

type Props = {
  companyId: string;
  selectedIds: string[];
  /** Selected ids that aren't in the currently loaded/filtered collection. */
  hiddenCount: number;
  showOnlySelected: boolean;
  onToggleShowOnlySelected: () => void;
  onClear: () => void;
  /** Ids the server no longer finds/allows for this selection — drop them. */
  onRemoveMissing: (ids: string[]) => void;
};

const buttonClass =
  "inline-flex items-center gap-1.5 rounded-sm border border-[#E6E1DB] bg-white px-3 py-1.5 text-sm text-[#3F3227] transition-colors hover:bg-gray-50 whitespace-nowrap";

function TotalStat({
  testId,
  label,
  value,
  isLoading,
  isError,
}: {
  testId: string;
  label: string;
  value: number | null | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  const isNegative = typeof value === "number" && value < 0;
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-xs text-gray-500">{label}</span>
      <span
        data-testid={`selection-total-${testId}`}
        className={clsx(
          "text-sm font-medium",
          isLoading && "opacity-50",
          isError && "text-gray-400",
          !isError && isNegative && "text-red-600",
          !isError && !isNegative && "text-gray-900"
        )}
      >
        {isError || value === null || value === undefined ? (
          "—"
        ) : (
          <Currency value={value} isSymbol isDecimal />
        )}
      </span>
    </div>
  );
}

const CustomersSelectionBar = forwardRef<HTMLDivElement, Props>(function CustomersSelectionBar(
  {
    companyId,
    selectedIds,
    hiddenCount,
    showOnlySelected,
    onToggleShowOnlySelected,
    onClear,
    onRemoveMissing,
  },
  ref
) {
  const { t } = useTranslation();
  // Keep the bar's right edge on the presence rail's left edge.
  const railWidth = usePresenceRailWidth(companyId);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [missingCount, setMissingCount] = useState<number | null>(null);
  // Guards against a slow, now-superseded request overwriting fresher totals.
  const requestIdRef = useRef(0);
  // `onRemoveMissing` is a fresh arrow from the parent every render; keeping
  // it in a ref keeps it out of the debounce effect's dependency list.
  const onRemoveMissingRef = useRef(onRemoveMissing);
  onRemoveMissingRef.current = onRemoveMissing;

  useEffect(() => {
    if (selectedIds.length === 0) {
      requestIdRef.current += 1;
      setTotals(null);
      setIsLoading(false);
      setIsError(false);
      setMissingCount(null);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    const timer = setTimeout(() => {
      summary(companyId, selectedIds)
        .then((data: { totals: Totals; missingIds: string[] }) => {
          if (requestIdRef.current !== requestId) return; // stale response
          setTotals(data.totals);
          setIsError(false);
          // Removing the missing ids changes the selection and triggers the
          // next fetch, which comes back clean — so the notice is NOT reset on
          // a clean response, or it would vanish 300 ms after it appeared.
          if (data.missingIds && data.missingIds.length > 0) {
            setMissingCount(data.missingIds.length);
            onRemoveMissingRef.current(data.missingIds);
          }
        })
        .catch(() => {
          if (requestIdRef.current !== requestId) return;
          setTotals(null);
          setIsError(true);
        })
        .finally(() => {
          if (requestIdRef.current === requestId) setIsLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [companyId, selectedIds]);

  if (selectedIds.length === 0) return null;

  return (
    <div
      ref={ref}
      data-testid="customers-selection-bar"
      className="fixed left-0 bottom-[var(--phone-bar-height,0px)] z-30 border-t border-[#E6E1DB] bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.08)] transition-[right] duration-200 ease-out"
      style={{ right: railWidth }}
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
        <div className="text-sm font-medium text-gray-900">
          {t("selected_count", { count: selectedIds.length })}
          {hiddenCount > 0 && (
            <span className="ml-1 font-normal text-gray-500">
              {t("selected_hidden_count", { count: hiddenCount })}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <TotalStat
            testId="turnover"
            label={t("sales_turnover_excl_vat")}
            value={totals?.turnover}
            isLoading={isLoading}
            isError={isError}
          />
          <TotalStat
            testId="purchase-turnover"
            label={t("purchase_turnover_excl_vat")}
            value={totals ? totals.purchaseTurnover : null}
            isLoading={isLoading}
            isError={isError}
          />
          <TotalStat
            testId="debt"
            label={t("debt_incl_vat")}
            value={totals?.debt}
            isLoading={isLoading}
            isError={isError}
          />
          <TotalStat
            testId="overdue-debt"
            label={t("overdue_debt_incl_vat")}
            value={totals?.overdueDebt}
            isLoading={isLoading}
            isError={isError}
          />
        </div>

        {isError && (
          <div className="text-sm text-red-600">
            {t("selection_totals_failed")}
          </div>
        )}
        {missingCount != null && (
          <div className="text-sm text-amber-600">
            {t("selection_removed_missing", { count: missingCount })}
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className={buttonClass}
            onClick={onToggleShowOnlySelected}
          >
            {showOnlySelected ? t("show_all_rows") : t("show_only_selected")}
          </button>
          <button type="button" className={buttonClass} onClick={onClear}>
            <X className="h-4 w-4" />
            {t("clear_selection")}
          </button>
        </div>
      </div>
    </div>
  );
});

export default CustomersSelectionBar;
